import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useGuestAuth } from '@/hooks/useGuestAuth';
import { useGuestServices } from '@/hooks/useGuestServices';
import { 
  Calendar, 
  MapPin, 
  Users, 
  CreditCard, 
  LogOut, 
  Bell,
  MessageCircle,
  Coffee,
  Car
} from 'lucide-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

export const GuestDashboard = () => {
  const { guestSession, guestLogout } = useGuestAuth();
  const { services, orders } = useGuestServices(guestSession?.guest.id, guestSession?.booking.hotel_id);

  if (!guestSession) return null;

  const { booking } = guestSession;
  const checkInDate = new Date(booking.check_in_date);
  const checkOutDate = new Date(booking.check_out_date);
  const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));

  const getStatusBadge = (status: string) => {
    const variants = {
      'pending': 'secondary',
      'confirmed': 'default',
      'checked_in': 'default',
      'checked_out': 'outline',
      'cancelled': 'destructive'
    } as const;
    
    const labels = {
      'pending': 'Ожидает подтверждения',
      'confirmed': 'Подтверждено',
      'checked_in': 'Заселен',
      'checked_out': 'Выселен',
      'cancelled': 'Отменено'
    };

    return (
      <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>
        {labels[status as keyof typeof labels] || status}
      </Badge>
    );
  };

  const quickActions = [
    {
      icon: Coffee,
      title: "Заказать в номер",
      description: "Еда и напитки",
      count: services.filter(s => s.category === 'restaurant').length
    },
    {
      icon: Car,
      title: "Трансфер",
      description: "Заказать такси",
      count: services.filter(s => s.category === 'transport').length
    },
    {
      icon: MessageCircle,
      title: "Обратная связь",
      description: "Оценить сервис",
      count: 0
    },
    {
      icon: Bell,
      title: "Уведомления",
      description: "Важная информация",
      count: 0
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Добро пожаловать, Гость!
              </h1>
              <p className="text-sm text-muted-foreground">
                Номер брони: {booking.booking_reference || `#${booking.id.slice(-8)}`}
              </p>
            </div>
            <Button variant="outline" onClick={guestLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Выйти
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Booking Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Информация о бронировании
              {getStatusBadge(booking.status)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Заезд</p>
                  <p className="text-sm text-muted-foreground">
                    {format(checkInDate, 'dd MMMM yyyy', { locale: ru })}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Выезд</p>
                  <p className="text-sm text-muted-foreground">
                    {format(checkOutDate, 'dd MMMM yyyy', { locale: ru })}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Гостей</p>
                  <p className="text-sm text-muted-foreground">{booking.number_of_guests}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Сумма</p>
                  <p className="text-sm text-muted-foreground">{booking.total_amount} ₽</p>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm">
                <strong>Тип номера:</strong> {booking.room_type}
              </p>
              <p className="text-sm">
                <strong>Ночей:</strong> {nights}
              </p>
              {booking.special_requests && (
                <p className="text-sm mt-2">
                  <strong>Особые пожелания:</strong> {booking.special_requests}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {quickActions.map((action, index) => (
            <Card key={index} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <action.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{action.title}</p>
                    <p className="text-xs text-muted-foreground">{action.description}</p>
                  </div>
                  {action.count > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {action.count}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Orders */}
        {orders.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Мои заказы</CardTitle>
              <CardDescription>
                История ваших заказов услуг отеля
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {orders.slice(0, 5).map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Заказ #{order.id.slice(-8)}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(order.created_at), 'dd.MM.yyyy HH:mm')}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant={
                        order.status === 'completed' ? 'default' : 
                        order.status === 'pending' ? 'secondary' : 'outline'
                      }>
                        {order.status === 'pending' ? 'Обрабатывается' :
                         order.status === 'confirmed' ? 'Подтвержден' :
                         order.status === 'completed' ? 'Выполнен' : 'Отменен'}
                      </Badge>
                      {order.total_amount && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {order.total_amount} ₽
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {orders.length > 5 && (
                <Button variant="outline" className="w-full mt-4">
                  Показать все заказы
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};